
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import CollapseHeader from '../../core/common/collapse-header/collapse-header';
import { all_routes } from "../router/all_routes";
import Table from "../../core/common/dataTable/index";
import { AppDispatch } from "../../core/data/redux/store";
import { useDispatch, useSelector } from "react-redux";
import { createHoliday, deleteHoliday, fetchAllHolidays, updateHoliday } from "../../core/data/redux/holiday/holidayAction";
import { formatDate } from "../../core/utils/configFormatters";
import DeleteModal1 from "../../core/modals/deleteModal1";
import { filterData } from "../../core/common/GlobalFunctions/tableFilters";
import { exportToCSV, exportToExcel, exportToPDF } from "../../core/common/GlobalFunctions/exportTableData";
import { DatePicker, Drawer, message } from "antd";
import { useAppConfig } from "../../customhooks/useAppConfig";
import dayjs from "dayjs";
import { decryptData } from "../../core/utils/encryptUtils";
import timeZonesWithHolidays from "../administration/timezone";

interface HolidaysProps {
  status?: "active" | "inactive";
  showOnlyTable?: boolean;
}

const Holidays: React.FC<HolidaysProps> = ({ status, showOnlyTable }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [selectedStatus, setSelectedStatus] = useState("active");
  const [selectedSort, setSelectedSort] = useState<any>("Recently Added");
  const [tableKey, setTableKey] = useState(0);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
  const [modalType, setModalType] = useState<"edit" | "create" | "">("");
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [holidayTitle, setHolidayTitle] = useState<string>("");
  const [holidayStartDate, setHolidayStartDate] = useState<string>("");
  const [holidayEndDate, setHolidayEndDate] = useState<string>("");
  const [holidayDescription, setHolidayDescription] = useState<string>("");
  const { user } = useSelector((state: any) => state.auth);
  const [holidayStatus, setHolidayStatus] = useState("active");
  const { holidays = [], loading } = useSelector((state: any) => state.holiday);
  const holidayId = selectedRowData?.Id;
  const [holidayType, setHolidayType] = useState("");
  const [errors, setErrors] = useState<any>({});

  const encryptedPermissions = localStorage.getItem('currentPermissions');
  const permissions: string[] = Array.isArray(decryptData(encryptedPermissions || '')) ? decryptData(encryptedPermissions || '') : [];

  const canAdd =
    permissions.includes("Add") || permissions.includes("AllowAll");
  const canView =
    permissions.includes("View") || permissions.includes("AllowAll");
  const canEdit =
    permissions.includes("Edit") || permissions.includes("AllowAll");
  const canDelete =
    permissions.includes("Delete") || permissions.includes("AllowAll");
  const canExport =
    permissions.includes("Export") || permissions.includes("AllowAll");

  useEffect(() => {
    if (!holidays.length) {
      dispatch(fetchAllHolidays());
    }
  }, [dispatch, holidays.length]);


  const formatFormDate = (dateStr: any) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    if (selectedRowData && modalType === "edit") {
      setHolidayTitle(selectedRowData?.Name || "");
      setHolidayType(selectedRowData?.HolidayType || "");
      setHolidayDescription(selectedRowData?.Description || "");
      setHolidayStatus(selectedRowData?.Status || "active");

      setHolidayStartDate(formatFormDate(selectedRowData?.StartDate));
      setHolidayEndDate(formatFormDate(selectedRowData?.EndDate));
    }
  }, [selectedRowData, modalType]);

  useEffect(() => {
    // only run this effect when creating, not editing
    if (modalType === "create" && holidayType === "National_holiday" && holidayTitle) {
      const selectedHoliday = nationalHolidays.find(
        (h: any) => h.name === holidayTitle
      );
      if (selectedHoliday) {
        setHolidayStartDate(selectedHoliday.date);
        setHolidayEndDate(selectedHoliday.date);
      }
    }
  }, [holidayTitle, holidayType, modalType]);


  const resetFields = () => {
    setHolidayTitle("");
    setHolidayDescription("");
    setHolidayStartDate("");
    setHolidayEndDate("");
    setHolidayType("");
    setSelectedRowData(null);
    setHolidayStatus("active");
  };


  const showModal = (type: "edit" | "create") => {
    setModalType(type);
    setIsModalVisible(true);
  };
  const handleCancel = () => {
    setIsModalVisible(false);
    setModalType("");
    resetFields();
    setErrors({});
  };

  const handleEditHoliday = (record: any): void => {
    setSelectedRowData(record);
  };


  useEffect(() => {
    if (holidayType === "National_holiday" && holidayTitle) {
      const selectedHoliday = nationalHolidays.find(
        (h: any) => h.name === holidayTitle
      );
      if (selectedHoliday) {
        setHolidayStartDate(selectedHoliday.date);
        setHolidayEndDate(selectedHoliday.date);
      }
    }
  }, [holidayTitle, holidayType]);

  const validateForm = () => {
    let formIsValid = true;

    let newErrors: any = {
      Name: "",
      holidayDate: "",
      Description: "",
      HolidayType: '',
    };

    if (!String(holidayTitle || "").trim()) {
      formIsValid = false;
      newErrors.Name = "Holiday title is required.";
    }

    if (!holidayType.trim()) {
      formIsValid = false;
      newErrors.HolidayType = "Holiday Type is required.";
    }

    if (!holidayStartDate) {
      formIsValid = false;
      newErrors.holidayStartDate = "Start date is required.";
    }
    if (!holidayEndDate) {
      formIsValid = false;
      newErrors.holidayEndDate = "End date is required.";
    }
    if (!holidayStatus) {
      formIsValid = false;
      newErrors.Status = "Status is required.";
    }

    setErrors(newErrors);
    return formIsValid;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const holidayData = {
      Name: holidayTitle,
      StartDate: holidayStartDate,
      EndDate: holidayEndDate,
      Description: holidayDescription,
      Status: holidayStatus,
      CreatedBy: user?.UserId,
      ModifiedBy: user?.UserId,
      HolidayType: holidayType,
    };

    if (modalType === "create") {
      const result = await dispatch(createHoliday(holidayData));
      if (result.meta.requestStatus === "fulfilled") {
        message.success("Holiday created successfully");
        await dispatch(fetchAllHolidays());
        setTableKey((prevKey) => prevKey + 1);
        resetFields();
      } else if (result.meta.requestStatus === "rejected") {
        message.warning(result.payload.error);
      }
    } else if (modalType === "edit" && holidayId) {
      const result = await dispatch(
        updateHoliday({ data: holidayData, holidayId })
      );
      if (result.meta.requestStatus === "fulfilled") {
        message.success("Holiday updated successfully");
        await dispatch(fetchAllHolidays());
        setTableKey((prevKey) => prevKey + 1);
        resetFields();
      } else if (result.meta.requestStatus === "rejected") {
        message.warning(result.payload.error);
      }
    }
    handleCancel();
  };

  const handleDeleteHoliday = async (designationId: number) => {
    try {
      const result = await dispatch(deleteHoliday({ holidayId }));
      if (result.meta.requestStatus === "fulfilled") {
        message.success("Holiday deleted successfully");
        setIsDeleteModalVisible(false);
        await dispatch(fetchAllHolidays());
        setTableKey((prevKey) => prevKey + 1);
      } else {
        message.warning(result.payload.error);
      }
    } catch (error) {
      message.error("Something went wrong");
    }
  };

  const routes = all_routes;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const data = holidays ? holidays : [];


  const exportHolidays = data.map((item: any, index: number) => ({
    "S.No": index + 1,
    Holiday: item.Name || "N/A",
    "Holiday Description": item.Description || "N/A",
    StartDate: item.StartDate ? new Date(item.StartDate).toLocaleDateString("en-IN") : "N/A",
    EndDate: item.EndDate ? new Date(item.EndDate).toLocaleDateString("en-IN") : "N/A",
    "Holiday Type": item.HolidayType || "N/A",
  }));

  const filteredData = useMemo(() => {
    const filters =
      selectedStatus && selectedStatus !== "All"
        ? { Status: selectedStatus }
        : {};
    return filterData(
      data || [],
      filters,
      "CreatedAt",
      "CreatedAt",
      selectedSort
    );
  }, [dispatch, data, selectedStatus, selectedSort]);

  useEffect(() => {
    setTableKey((prevKey) => prevKey + 1);
  }, [dispatch, selectedStatus, selectedSort, holidays]);

  const config = useAppConfig();
  if (!config) return null;

  const selectedCountry = config?.country;

  const matchedCountry = timeZonesWithHolidays.find(
    (tz) => tz.country === selectedCountry
  );

  const nationalHolidays = matchedCountry?.national_holidays || [];

  const columns = [
    {
      title: "Title",
      dataIndex: "Name",
      render: (text: string) => (
        <h6 className="fw-medium">
          <Link to="#">{text}</Link>
        </h6>
      ),
      sorter: (a: any, b: any) => a.Name.length - b.Name.length,
    },
    {
      title: "Start Date",
      dataIndex: "StartDate",
      render: (text: string) => formatDate(text, config),
      sorter: (a: any, b: any) => a.StartDate.length - b.StartDate.length,
    },
    {
      title: "End Date",
      dataIndex: "EndDate",
      render: (text: string) => formatDate(text, config),
      sorter: (a: any, b: any) => a.EndDate.length - b.EndDate.length,
    },
    {
      title: "Holiday Type",
      dataIndex: "HolidayType",
      sorter: (a: any, b: any) => a.HolidayType.length - b.HolidayType.length,
    },
    {
      title: "Description",
      dataIndex: "Description",
      sorter: (a: any, b: any) => a.Description.length - b.Description.length,
    },
    {
      title: "Status",
      dataIndex: "Status",
      render: (text: string) => (
        <span
          className={`badge ${text === "active" ? "badge-success" : "badge-danger"
            } d-inline-flex align-items-center badge-xs`}
        >
          <i className="ti ti-point-filled me-1" />
          {text?.charAt(0).toUpperCase() + text.slice(1)}
        </span>
      ),
      sorter: (a: any, b: any) => a.Status.length - b.Status.length,
    },
    {
      title: "Action",
      dataIndex: "actions",
      render: (text: string, record: any) => (
        <div className="action-icon d-inline-flex">
          {/* Edit Button - Disabled if no edit permission */}
          <button
            onClick={() => {
              if (canEdit) {
                showModal("edit");
                handleEditHoliday(record);
              }
            }}
            className="me-2"
            disabled={!canEdit}
            style={{ cursor: canEdit ? "pointer" : "not-allowed" }}
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            title={canEdit ? "Edit" : "No permission to edit"}
          >
            <i className="ti ti-edit" />
          </button>

          {/* Delete Button - Disabled if no delete permission or record is inactive */}
          <button
            onClick={() => {
              if (canDelete && record.Status !== "inactive") {
                setSelectedRowData(record);
                setIsDeleteModalVisible(true);
              }
            }}
            className="me-2"
            disabled={!canDelete || record.Status === "inactive"}
            style={{ cursor: !canDelete || record.Status === "inactive" ? "not-allowed" : "pointer", }}
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            title={!canDelete
              ? "No permission to delete"
              : record.Status === "inactive"
                ? "Inactive record"
                : "Delete"
            }
          >
            <i className="ti ti-trash" />
          </button>
        </div>
      ),
    },
  ];
  if (showOnlyTable) {
    return (
      <Table
        key={tableKey}
        dataSource={filteredData}
        columns={columns}
        Selection={true}
      />
    )
  }
  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Holidays</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Holidays
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
              {canExport && (
                <div className="me-2 mb-2">
                  <div className="dropdown">
                    <Link
                      to="#"
                      className="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                      data-bs-toggle="dropdown"
                    >
                      <i className="ti ti-file-export me-1" />
                      Export
                    </Link>
                    <ul className="dropdown-menu  dropdown-menu-end p-3">
                      <li>
                        <button
                          onClick={() =>
                            exportToCSV(exportHolidays, "holiday_data")
                          }
                          className="dropdown-item rounded-1"
                        >
                          <i className="ti ti-file-type-pdf me-1" />
                          Export as CSV
                        </button>
                      </li>
                      <li>
                        <button
                          onClick={() =>
                            exportToExcel(exportHolidays, "holiday_data")
                          }
                          className="dropdown-item rounded-1"
                        >
                          <i className="ti ti-file-type-xls me-1" />
                          Export as Excel{" "}
                        </button>
                      </li>
                      <li>
                        <button
                          onClick={() =>
                            exportToPDF(exportHolidays, "holiday_data")
                          }
                          className="dropdown-item rounded-1"
                        >
                          <i className="ti ti-file-type-pdf me-1" />
                          Export as PDF
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              )}
              {canAdd && (
                <div className="mb-2">
                  <button
                    onClick={() => showModal("create")}
                    className="btn btn-primary d-flex align-items-center"
                  >
                    <i className="ti ti-circle-plus me-2" />
                    Add Holiday
                  </button>
                </div>
              )}
              <div className="head-icons ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>
          {/* /Breadcrumb */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <h5>Holiday List</h5>
              <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                <div className="dropdown me-3">
                  {/* <Link
                    to="#"
                    className="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    Department
                  </Link> */}
                  <ul className="dropdown-menu  dropdown-menu-end p-3">
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Finance
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Application Development
                      </Link>
                    </li>
                  </ul>
                </div>
                <div className="dropdown me-3">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    {selectedStatus
                      ? selectedStatus?.charAt(0).toUpperCase() +
                      selectedStatus.slice(1)
                      : "Select Status"}
                  </Link>
                  <ul className="dropdown-menu  dropdown-menu-end p-3">
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedStatus("All")}
                        className="dropdown-item rounded-1"
                      >
                        All
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedStatus("active")}
                        className="dropdown-item rounded-1"
                      >
                        Active
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedStatus("inactive")}
                        className="dropdown-item rounded-1"
                      >
                        Inactive
                      </Link>
                    </li>
                  </ul>
                </div>
                <div className="dropdown">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    {selectedSort
                      ? `Sort By : ${selectedSort}`
                      : "Sort By : Last 7 Days"}
                  </Link>
                  <ul className="dropdown-menu dropdown-menu-end p-3">
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedSort("Recently Added")}
                        className="dropdown-item rounded-1"
                      >
                        Recently Added
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedSort("Ascending")}
                        className="dropdown-item rounded-1"
                      >
                        Ascending
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedSort("Descending")}
                        className="dropdown-item rounded-1"
                      >
                        Descending
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedSort("Last Month")}
                        className="dropdown-item rounded-1"
                      >
                        Last Month
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedSort("Last 7 Days")}
                        className="dropdown-item rounded-1"
                      >
                        Last 7 Days
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="card-body p-0">
              <Table
                key={tableKey}
                dataSource={filteredData}
                columns={columns}
                Selection={true}
              />
            </div>
          </div>
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2024 - 2025© hrmtree.</p>
          <p>
            Developed By{" "}
            <Link to="#" className="text-primary">
              Leadchain Systems
            </Link>
          </p>
        </div>
      </div>
      {/* /Page Wrapper */}

      <Drawer
        title={modalType === "edit" ? "Edit Holiday" : "Add Holiday"}
        placement="right"
        width={400}
        onClose={handleCancel}
        visible={isModalVisible}
        style={{ marginTop: 50, paddingBottom: 50 }}
      >
        <form>
          <div className="modal-body pb-0">
            <div className="row">
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="form-label">
                    Holiday Type<span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={holidayType}
                    onChange={(e) => setHolidayType(e.target.value)}
                  >
                    <option value="">Select Holiday Type</option>
                    <option value="fixed">Fixed</option>
                    <option value="optional">Optional</option>
                    <option value="National_holiday">National Holiday</option>
                  </select>
                  {errors.HolidayType && (
                    <div style={{ color: "red" }}>{errors.HolidayType}</div>
                  )}
                </div>
              </div>
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="form-label">
                    Title <span className="text-danger">*</span>
                  </label>

                  {holidayType === "National_holiday" ? (
                    <>
                      <select
                        className="form-select mb-2"
                        value={holidayTitle}
                        onChange={e => setHolidayTitle(e.target.value)}
                      >
                        <option value="">Select a holiday</option>
                        {nationalHolidays.map((h: any, idx: any) => (
                          <option key={idx} value={h.name}>{h.name}</option>
                        ))}
                      </select>
                    </>
                  ) : (
                    <input
                      type="text"
                      className="form-control"
                      value={holidayTitle}
                      onChange={e => setHolidayTitle(e.target.value)}
                    />
                  )}

                  {errors.Name && <div style={{ color: "red" }}>{errors.Name}</div>}
                </div>
              </div>
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="form-label">
                    Start Date <span className="text-danger">*</span>
                  </label>
                  <div className="input-icon-end position-relative">
                    <DatePicker
                      value={holidayStartDate ? dayjs(holidayStartDate) : null}
                      onChange={(date) => setHolidayStartDate(date ? date.format("YYYY-MM-DD") : "")}
                      format="DD/MM/YYYY"
                      style={{ width: "100%" }}
                    />
                    {errors.holidayStartDate && (
                      <div style={{ color: "red" }}>{errors.holidayStartDate}</div>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="form-label">
                    End Date <span className="text-danger">*</span>
                  </label>
                  <div className="input-icon-end position-relative">
                    <DatePicker
                      value={holidayEndDate ? dayjs(holidayEndDate) : null}
                      onChange={(date) => setHolidayEndDate(date ? date.format("YYYY-MM-DD") : "")}
                      format="DD/MM/YYYY"
                      style={{ width: "100%" }}
                    />
                    {errors.holidayEndDate && (
                      <div style={{ color: "red" }}>{errors.holidayEndDate}</div>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="form-label">
                    Status <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    value={holidayStatus}
                    onChange={(e) => setHolidayStatus(e.target.value)}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
              {errors.Status && (
                <div style={{ color: "red" }}>{errors.Status}</div>
              )}
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="form-label">Description </label>
                  <textarea
                    className="form-control"
                    rows={2}
                    defaultValue={""}
                    value={holidayDescription}
                    onChange={(e) => setHolidayDescription(e.target.value)}
                  />
                  {errors.Description && (
                    <div style={{ color: "red" }}>{errors.Description}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-light me-2"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="btn btn-primary"
            >
              {loading && (
                <span className="spinner-border spinner-border-sm me-2" />
              )}
              {modalType === "edit" ? "Update Holiday" : "Add Holiday"}
            </button>
          </div>
        </form>

      </Drawer>

      <DeleteModal1
        isVisible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onConfirm={() =>
          selectedRowData?.Id && handleDeleteHoliday(selectedRowData.Id)
        }
      />
    </>
  );
};

export default Holidays;